import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronRight, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import ImageIcon from '@/components/common/icons/Image'
import VideoIcon from '@/components/common/icons/Video'
import ActionButtons from './ActionButtons'
import CardDetails from './CardDetails'

const Card = ({
    card,
    isExpanded,
    onToggleExpanded,
    handleInputChange,
    handleDeleteCard,
    goToStep,
    openThumbnailEditor,
    selectedAsset,
    isUpdatingThumbnail,
    setStepContentData,
    cards,
    handleApplyToAllCards,
    isApplyingToAll,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : 0,
    }

    const isVideo = (asset) => asset.mime_type.startsWith('video')
    const getThumbnailUrl = (asset) => {
        let { url } = asset
        if (asset.thumbnails && asset.thumbnails.length > 0) url = asset.thumbnails[0].url

        return `${import.meta.env.VITE_S3_BUCKET_URL}${url}`
    }

    const navigateCropPage = () => {
        const existingCardIds = cards.map((c) => c.id)
        setStepContentData(
            { assetId: card.id, replaceAssetPosition: card.order, existingCardIds },
            'setMediaCrop',
        )
        goToStep('setMediaCrop')
    }

    const navigateTrimVideo = () => {
        setStepContentData({ assetId: card.id }, 'videoTrim')
        goToStep('videoTrim')
    }

    const goToMediaLibraryReplace = () => {
        const existingCardIds = cards.map((c) => c.id)
        setStepContentData(
            { replaceAssetId: card.id, replaceAssetPosition: card.order, existingCardIds },
            'uploadAssets',
        )
        goToStep('uploadAssets')
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn('tw:relative tw:overflow-hidden tw:border-b tw:p-4', {
                'tw:bg-gray-500': isExpanded,
                'tw:opacity-50': isDragging,
            })}
        >
            {/* Card Header */}
            <div
                className="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:cursor-pointer"
                onClick={(e) => {
                    e.stopPropagation()
                    onToggleExpanded(card.id)
                }}
            >
                <div className="tw:flex tw:items-center tw:gap-2">
                    <div {...attributes} {...listeners}>
                        <GripVertical size={16} className="tw:text-blue-300 tw:cursor-grab" />
                    </div>
                    <button
                        className="tw:p-0 tw:bg-transparent tw:border-0 tw:text-white"
                        onClick={() => onToggleExpanded(card.id)}
                    >
                        <ChevronRight
                            size={18}
                            className={cn(
                                'tw:text-blue-300 tw:transition-transform tw:duration-200 tw:ease-in-out',
                                isExpanded && 'tw:rotate-90',
                            )}
                        />
                    </button>
                </div>

                {/* Media Preview */}
                <div className="tw:relative tw:!w-10 tw:!h-10">
                    <img
                        alt={card.name}
                        src={getThumbnailUrl(card)}
                        className="tw:w-full tw:h-full tw:rounded-xs object-cover"
                    />
                    {selectedAsset?.id === card.id && isUpdatingThumbnail && (
                        <div
                            className={cn(
                                'tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:bg-background',
                                'tw:rounded-xs tw:p-1 tw:w-5 tw:h-5 tw:flex tw:items-center tw:justify-center tw:opacity-70',
                            )}
                        >
                            <LoaderCircle size={16} className="tw:text-white tw:animate-spin" />
                        </div>
                    )}
                </div>

                {/* File Name */}
                <div className="tw:flex-1">
                    <div className="tw:text-sm tw:text-white tw:flex tw:items-center tw:gap-2">
                        {isVideo(card) ? <VideoIcon /> : <ImageIcon />}
                        {card.name}
                    </div>
                    <div className="tw:text-gray-200 tw:text-sm tw:mt-1">
                        {card.headline || 'Enter headline and description'}
                    </div>
                </div>

                <ActionButtons
                    asset={card}
                    goToMediaLibrary={goToMediaLibraryReplace}
                    gotToCropPage={navigateCropPage}
                    openThumbnailEditor={() => openThumbnailEditor(card)}
                    gotToVideoTrim={navigateTrimVideo}
                    handleDeleteCard={handleDeleteCard}
                />
            </div>

            {/* Expanded Form */}
            <div
                className={cn(
                    'tw:overflow-hidden tw:transition-all tw:duration-300 tw:ease-in-out',
                    isExpanded ? 'tw:max-h-[1000px] tw:opacity-100' : 'tw:max-h-0 tw:opacity-0',
                )}
            >
                <div className="tw:pt-4">
                    <CardDetails
                        card={card}
                        handleInputChange={handleInputChange}
                        handleApplyToAllCards={handleApplyToAllCards}
                        isApplyingToAll={isApplyingToAll}
                        allCards={cards}
                    />
                </div>
            </div>
        </div>
    )
}

export default Card
